import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { employerApi, ApiRequestError, type EmployerTimesheet } from '@pinchcrew/core'
import { t } from '../i18n'

export const useTimesheetsStore = defineStore('employer-timesheets', () => {
  const all = ref<EmployerTimesheet[]>([])
  const loading = ref(false)
  const busyId = ref<string | null>(null)
  const error = ref<string | null>(null)

  const pending = computed(() => all.value.filter(x => x.status === 'pending'))
  const settled = computed(() => all.value.filter(x => x.status !== 'pending'))

  /** What approving everything pending would cost — shown before confirming. */
  const pendingCost = computed(() =>
    pending.value.reduce((sum, x) => sum + Number(x.employer_total), 0),
  )

  async function load() {
    loading.value = true
    try { all.value = await employerApi.listTimesheets() } finally { loading.value = false }
  }

  async function approve(id: string) {
    busyId.value = id
    error.value = null
    try {
      const updated = await employerApi.approveTimesheet(id)
      const i = all.value.findIndex(x => x.id === id)
      if (i >= 0) all.value[i] = updated
      return true
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : t('common.somethingWrong')
      return false
    } finally {
      busyId.value = null
    }
  }

  async function approveAll() {
    loading.value = true
    try {
      const result = await employerApi.approveAllPending()
      await load()
      return result.approved
    } finally {
      loading.value = false
    }
  }

  async function adjust(id: string, hours: number, reason: string) {
    busyId.value = id
    error.value = null
    try {
      const updated = await employerApi.adjustTimesheet(id, Math.round(hours * 60), reason)
      const i = all.value.findIndex(x => x.id === id)
      if (i >= 0) all.value[i] = updated
      return true
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : t('common.somethingWrong')
      return false
    } finally {
      busyId.value = null
    }
  }

  return { all, loading, busyId, error, pending, settled, pendingCost, load, approve, approveAll, adjust }
})
